import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Code, Server, Database, Shield, Cpu, Globe, Wrench, Layout } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/App";
import SkillBar from "@/components/SkillBar";
import ServiceCard from "@/components/ServiceCard";
import TestimonialsSection from "@/components/TestimonialsSection";
import NewsletterSection from "@/components/NewsletterSection";
import JoinTeamModal from "@/components/JoinTeamModal";
import SEO from "@/components/SEO";
import { PersonJsonLd, WebSiteJsonLd, ReviewsJsonLd } from "@/components/JsonLd";
import LeaveReviewModal from "@/components/LeaveReviewModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PortfolioPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isTeamMember = user?.role === "admin" || user?.role === "editor";
  const canLeaveReview = !!user && !isTeamMember;
  const [testimonials, setTestimonials] = useState([]);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);

  const fetchTestimonials = () => {
    fetch(`${API}/testimonials`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setTestimonials)
      .catch(() => setTestimonials([]));
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const skills = [
    { name: "Visualisation de données", percentage: 98 },
    { name: "PHP", percentage: 90 },
    { name: "C & C++", percentage: 90 },
    { name: "JavaScript", percentage: 85 },
    { name: "Bases de données (SQL/NoSQL)", percentage: 85 },
    { name: "Linux & Systèmes", percentage: 85 },
    { name: "ReactJS", percentage: 80 },
    { name: "Node.js", percentage: 80 },
    { name: "Sécurité informatique", percentage: 75 },
    { name: "Cloud & Déploiement", percentage: 75 },
    { name: "Python", percentage: 70 },
  ];

  const services = [
    { icon: Globe, titleKey: "services.webDev", descKey: "services.webDevDesc" },
    { icon: Shield, titleKey: "services.security", descKey: "services.securityDesc" },
    { icon: Server, titleKey: "services.integration", descKey: "services.integrationDesc" },
    { icon: Layout, titleKey: "services.conception", descKey: "services.conceptionDesc" },
    { icon: Wrench, titleKey: "services.maintenance", descKey: "services.maintenanceDesc" },
    { icon: Database, titleKey: "services.database", descKey: "services.databaseDesc" },
  ];

  return (
    <>
      <SEO
        title="Portfolio"
        description="Tsitsok Michel Berger - Ingénieur Logiciel Senior & Consultant. Découvrez mes compétences, services et réalisations en développement web et mobile."
        type="profile"
        tags={["portfolio", "développeur", "ingénieur logiciel", "consultant", "React", "Node.js", "Python"]}
      />
      <PersonJsonLd
        image={[
          "https://micheltsitsok.com/photos/michel-suit.jpg",
          "https://micheltsitsok.com/photos/michel-traditional.png",
          "https://micheltsitsok.com/photos/michel-casual.png",
        ]}
        knowsAbout={[
          "Développement Web",
          "Développement Mobile",
          "ReactJS",
          "Node.js",
          "Python",
          "PHP",
          "C++",
          "Bases de données SQL/NoSQL",
          "Sécurité informatique",
          "Cloud & Déploiement",
          "Linux",
          "Visualisation de données",
        ]}
      />
      <WebSiteJsonLd />
      <ReviewsJsonLd testimonials={testimonials} />
      <div className="min-h-screen" data-testid="portfolio-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] md:min-h-screen flex items-center justify-center pt-16 md:pt-20 pb-16 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-background" />
          <div className="absolute top-1/4 right-1/4 w-48 md:w-96 h-48 md:h-96 bg-muted/50 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 w-48 md:w-96 h-48 md:h-96 bg-muted/30 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="text-center lg:text-left order-2 lg:order-1"
            >
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-foreground font-medium mb-2 md:mb-4 text-sm md:text-base"
              >
                {t("hero.role")}
              </motion.p>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 md:mb-4"
              >
                <span className="text-foreground">Tsitsok Michel</span>{" "}
                <span className="text-gradient">Berger</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-base md:text-lg text-muted-foreground mb-3 md:mb-4"
              >
                {t("hero.degree")}
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-sm md:text-base text-muted-foreground mb-6 md:mb-8 max-w-lg mx-auto lg:mx-0"
              >
                {t("hero.bio")}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4 justify-center lg:justify-start"
              >
                {!isTeamMember && (
                  <>
                    <Link to="/contact">
                      <Button
                        data-testid="hero-contact-btn"
                        className="w-full sm:w-auto rounded-full px-6 md:px-8 py-5 md:py-6 text-sm md:text-base"
                      >
                        {t("hero.contactBtn")}
                        <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      data-testid="hero-cv-btn"
                      className="w-full sm:w-auto rounded-full px-6 md:px-8 py-5 md:py-6 text-sm md:text-base border-border hover:bg-secondary"
                      asChild
                    >
                      <a href="/documents/CV_Michel_Berger.pdf" download="CV_Michel_Berger.pdf">
                        <Download className="mr-2 h-4 w-4 md:h-5 md:w-5" />
                        {t("hero.downloadCV")}
                      </a>
                    </Button>
                  </>
                )}
                <JoinTeamModal />
                {isTeamMember && user?.role === "admin" && (
                  <Link to="/admin">
                    <Button
                      data-testid="hero-admin-btn"
                      className="w-full sm:w-auto rounded-full px-6 md:px-8 py-5 md:py-6 text-sm md:text-base"
                    >
                      Accéder au Dashboard
                      <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
                    </Button>
                  </Link>
                )}
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="relative flex justify-center lg:justify-end order-1 lg:order-2"
            >
              <div className="relative flex flex-col items-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-muted rounded-full blur-2xl opacity-50 scale-110" />
                  <div className="relative w-52 h-52 sm:w-64 sm:h-64 md:w-72 md:h-72 lg:w-80 lg:h-80 xl:w-96 xl:h-96 rounded-full p-1 border-2 border-border">
                    <div className="w-full h-full rounded-full overflow-hidden bg-card flex items-center justify-center">
                      <img 
                        src="/photos/michel-suit.jpg" 
                        alt="Tsitsok Michel Berger - Ingénieur Logiciel & Consultant"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>

                {/* Badges sous la photo */}
                <div className="flex flex-wrap items-center justify-center gap-3 mt-5 md:mt-6">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, type: "spring" }}
                    className="bg-card rounded-full px-4 py-2 shadow-lg border border-border"
                    data-testid="badge-open-to-work"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
                      <span className="text-xs sm:text-sm font-medium text-foreground">Open to Work</span>
                    </div>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, type: "spring" }}
                    className="bg-card rounded-full px-4 py-2 shadow-lg border border-border"
                    data-testid="badge-experience"
                  >
                    <div className="flex items-center gap-2">
                      <Code className="h-4 w-4 text-foreground" />
                      <span className="text-xs sm:text-sm font-medium text-foreground">5+ Years XP</span>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 md:mb-16"
          >
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 md:mb-4">
              {t("skills.title")}
            </h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
              {t("skills.subtitle")}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
            {skills.map((skill, index) => (
              <SkillBar
                key={skill.name}
                name={skill.name}
                percentage={skill.percentage}
                delay={index * 0.1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 md:mb-16"
          >
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 md:mb-4">
              {t("services.title")}
            </h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
              {t("services.subtitle")}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {services.map((service, index) => (
              <ServiceCard
                key={service.titleKey}
                icon={service.icon}
                title={t(service.titleKey)}
                description={t(service.descKey)}
                delay={index * 0.1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Galerie Section - Photos pour SEO Google Images */}
      <section className="py-16 md:py-24 bg-secondary/30" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 md:mb-16"
          >
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 md:mb-4">
              En images
            </h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
              Tsitsok Michel Berger — quelques moments capturés.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto">
            <motion.figure
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="rounded-2xl overflow-hidden border border-border bg-card"
            >
              <img
                src="/photos/michel-suit.jpg"
                alt="Tsitsok Michel Berger en costume professionnel - Ingénieur Logiciel & Consultant"
                className="w-full h-72 md:h-80 object-cover"
                loading="lazy"
              />
              <figcaption className="p-3 text-xs text-muted-foreground text-center">
                Tsitsok Michel Berger — Portrait professionnel
              </figcaption>
            </motion.figure>

            <motion.figure
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="rounded-2xl overflow-hidden border border-border bg-card"
            >
              <img
                src="/photos/michel-traditional.png"
                alt="Tsitsok Michel Berger en tenue traditionnelle"
                className="w-full h-72 md:h-80 object-cover"
                loading="lazy"
              />
              <figcaption className="p-3 text-xs text-muted-foreground text-center">
                Tsitsok Michel Berger — Style traditionnel
              </figcaption>
            </motion.figure>

            <motion.figure
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="rounded-2xl overflow-hidden border border-border bg-card sm:col-span-2 lg:col-span-1"
            >
              <img
                src="/photos/michel-casual.png"
                alt="Tsitsok Michel Berger au travail sur son ordinateur portable - Ingénieur Logiciel"
                className="w-full h-72 md:h-80 object-cover"
                loading="lazy"
              />
              <figcaption className="p-3 text-xs text-muted-foreground text-center">
                Tsitsok Michel Berger — En plein développement
              </figcaption>
            </motion.figure>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsSection />

      {/* Leave a Review CTA - only for connected non-admin/editor users */}
      {canLeaveReview && (
        <section className="py-10 md:py-14" data-testid="leave-review-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <div className="bg-card border border-border rounded-2xl p-6 md:p-10">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-2">
                Vous avez travaillé avec moi ?
              </h3>
              <p className="text-sm md:text-base text-muted-foreground mb-5">
                Partagez votre expérience. Votre avis aidera d'autres clients à me faire confiance.
              </p>
              <Button
                onClick={() => setReviewModalOpen(true)}
                size="lg"
                data-testid="open-leave-review-btn"
              >
                Laisser un avis
              </Button>
            </div>
          </div>
        </section>
      )}

      <LeaveReviewModal
        open={reviewModalOpen}
        onOpenChange={setReviewModalOpen}
        onSubmitted={fetchTestimonials}
      />

      {/* Newsletter - Masqué pour l'équipe */}
      {!isTeamMember && <NewsletterSection />}

      {/* CTA Section - Masqué pour l'équipe */}
      {!isTeamMember && (
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative rounded-2xl md:rounded-3xl overflow-hidden bg-card border border-border"
            >
              <div className="relative px-6 md:px-12 py-12 md:py-16 text-center">
                <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 md:mb-4">
                  {t("cta.projectInMind")}
                </h2>
                <p className="text-muted-foreground mb-6 md:mb-8 max-w-xl mx-auto text-sm md:text-base">
                  {t("cta.projectSubtitle")}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                  <Link to="/contact">
                    <Button
                      data-testid="cta-contact-btn"
                      className="w-full sm:w-auto rounded-full px-6 md:px-8 py-5 md:py-6 text-sm md:text-base"
                    >
                      {t("cta.startProject")}
                      <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button
                      variant="outline"
                      data-testid="cta-blog-btn"
                      className="w-full sm:w-auto rounded-full px-6 md:px-8 py-5 md:py-6 text-sm md:text-base"
                    >
                      {t("cta.readBlog")}
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}
    </div>
    </>
  );
};

export default PortfolioPage;
